import {
    Alert,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Stack,
    TextField,
    Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { deleteUser, previewUserDeletion } from "../features/users/api";

const errorMessage = (err, fallback) => err?.response?.data?.error || fallback;

const summarizeNames = (items) => {
    const names = items.map((item) => item.name);
    return names.length > 5
        ? `${names.slice(0, 5).join(", ")} and ${names.length - 5} more`
        : names.join(", ");
};

/**
 * Confirms a user deletion: loads the backend dry run so the admin sees what
 * will be removed (and any blockers), and requires typing the user's email
 * (or id when there is no email) before the irreversible delete is allowed.
 */
const UserDeleteDialog = ({ user, onClose, onDeleted }) => {
    const [plan, setPlan] = useState(null);
    const [planError, setPlanError] = useState(null);
    const [confirmation, setConfirmation] = useState("");
    const [deleting, setDeleting] = useState(false);
    const [deleteError, setDeleteError] = useState(null);

    const confirmationTarget = user?.email || user?.id || "";

    useEffect(() => {
        if (!user) return undefined;
        let cancelled = false;
        setPlan(null);
        setPlanError(null);
        setConfirmation("");
        setDeleteError(null);

        previewUserDeletion(user.id)
            .then((response) => {
                if (!cancelled) setPlan(response.data);
            })
            .catch((err) => {
                if (!cancelled) {
                    setPlanError(errorMessage(err, "Failed to load what this deletion affects"));
                }
            });

        return () => {
            cancelled = true;
        };
    }, [user]);

    const blockedTeams = plan?.blockers?.managedTeams || [];
    const canDelete =
        Boolean(plan) &&
        blockedTeams.length === 0 &&
        confirmation.trim().toLowerCase() === confirmationTarget.toLowerCase() &&
        !deleting;

    const handleDelete = async () => {
        try {
            setDeleting(true);
            setDeleteError(null);
            await deleteUser(user.id);
            onDeleted(user);
        } catch (err) {
            console.error("Error deleting user:", err);
            setDeleteError(errorMessage(err, "Failed to delete user"));
        } finally {
            setDeleting(false);
        }
    };

    const handleClose = () => {
        if (!deleting) onClose();
    };

    return (
        <Dialog open={Boolean(user)} onClose={handleClose} maxWidth="sm" fullWidth>
            <DialogTitle>Delete user</DialogTitle>
            <DialogContent>
                <Stack spacing={2}>
                    <DialogContentText>
                        This permanently deletes <strong>{user?.name || confirmationTarget}</strong>
                        {user?.email ? ` (${user.email})` : ""}. Their account and sign-in are
                        removed, and this cannot be undone.
                    </DialogContentText>

                    {!plan && !planError && (
                        <Stack direction="row" spacing={1} alignItems="center">
                            <CircularProgress size={18} />
                            <Typography variant="body2">Checking what this affects…</Typography>
                        </Stack>
                    )}

                    {planError && <Alert severity="error">{planError}</Alert>}

                    {blockedTeams.length > 0 && (
                        <Alert severity="warning">
                            This user is the project manager of {summarizeNames(blockedTeams)}.
                            Assign another project manager or delete{" "}
                            {blockedTeams.length === 1 ? "that team" : "those teams"} first.
                        </Alert>
                    )}

                    {plan && blockedTeams.length === 0 && (
                        <Alert severity="info">
                            <Typography variant="body2">
                                Removed from {plan.projects.length} project
                                {plan.projects.length === 1 ? "" : "s"}
                                {plan.projects.length > 0
                                    ? `: ${summarizeNames(plan.projects)}`
                                    : ""}
                            </Typography>
                            <Typography variant="body2">
                                Removed from {plan.teams.length} team
                                {plan.teams.length === 1 ? "" : "s"}
                                {plan.teams.length > 0 ? `: ${summarizeNames(plan.teams)}` : ""}
                            </Typography>
                        </Alert>
                    )}

                    {plan && blockedTeams.length === 0 && (
                        <TextField
                            label={`Type ${confirmationTarget} to confirm`}
                            value={confirmation}
                            onChange={(event) => setConfirmation(event.target.value)}
                            autoComplete="off"
                            size="small"
                            fullWidth
                            autoFocus
                            disabled={deleting}
                        />
                    )}

                    {deleteError && <Alert severity="error">{deleteError}</Alert>}
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose} disabled={deleting}>
                    Cancel
                </Button>
                <Button
                    color="error"
                    variant="contained"
                    onClick={handleDelete}
                    disabled={!canDelete}
                    startIcon={deleting ? <CircularProgress size={16} color="inherit" /> : null}
                >
                    Delete user
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default UserDeleteDialog;
